import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  KindEnum,
  NotFoundException,
  TemplateEntity,
  TextFieldValue,
  WbGeneralPurposeEntityPicker,
} from '../../WbGeneralPurposeEntityPicker';
import { PolicyHandlerProvider } from './PolicyProvider/PolicyProvider';
import { useLatest } from 'react-use';
import { ApolloClient, useApolloClient } from '@apollo/client';
import { useApi } from '@backstage/core-plugin-api';
import { customAlertApiRef } from '../../../apis';
import { areArraysEqual } from '@agilelab/plugin-wb-platform-common';

type PolicyPickerProps = {
  title: string;
  description?: string | undefined;
  multiSelection?: boolean;
  freeSolo?: boolean;
  id?: string;
  onSelect: (val: any | undefined) => void;
  value: any | undefined;
  required?: boolean;
  disabled?: boolean;
  filters?: Record<string, any>;
  defaultValue?: any[];
};

export const entityHandler = (
  apolloClient: ApolloClient<any>,
  availableKinds: TemplateEntity[],
) =>
  new Map([
    [KindEnum.policy, new PolicyHandlerProvider(apolloClient, availableKinds)],
  ]);

export const PolicyPicker = ({
  title,
  description,
  required,
  disabled,
  freeSolo = false,
  onSelect,
  filters,
  value,
  defaultValue,
}: PolicyPickerProps) => {
  const onChangeRef = useLatest(onSelect);
  const alertApi = useApi(customAlertApiRef);

  const apolloClient = useApolloClient();

  const availableKinds: TemplateEntity[] = useMemo(() => {
    return [
      {
        type: KindEnum.policy,
        returnField: 'full',
        userFilters: ['search', 'status', 'severity'],
        filter: filters ?? undefined,
      },
    ];
  }, [filters]);

  const providerMapper = useMemo(
    () => entityHandler(apolloClient, availableKinds),
    [apolloClient, availableKinds],
  );
  const [currentValue, setCurrentValue] = useState<TextFieldValue[]>([]);

  useEffect(() => {
    if (value === '' || value === undefined || value === null) {
      setCurrentValue([]);
      return;
    }
  }, [value]);

  const fetchData = useCallback(
    async (ref: string) => {
      for (const [key, provider] of Array.from(providerMapper.entries())) {
        if (availableKinds.some(a => a.type === key)) {
          try {
            const res = await provider.resolve(ref);

            if (res) {
              return res;
            }
          } catch (err) {
            if (err instanceof NotFoundException) {
              return undefined;
            }

            alertApi.post({
              error: err,
              severity: 'error',
            });
            return undefined;
          }
        }
      }

      return undefined;
    },
    [alertApi, availableKinds, providerMapper],
  );

  const loadInitialValue = useCallback(
    (defaultValues: string[]) => {
      const checkCurrentValue = (val: string) => {
        const justResolved = currentValue?.find(cv => cv.value === val);
        return justResolved ? justResolved : null;
      };

      return defaultValues.map(async input => {
        const justResolved = checkCurrentValue(input);
        if (justResolved) {
          return justResolved;
        }

        const entity = await fetchData(input);
        return entity
          ? entity
          : {
              kind: KindEnum.arbitrary,
              label: input,
              value: input,
            };
      });
    },
    [currentValue, fetchData],
  );

  const handleSelect = useCallback(
    (values: TextFieldValue[] | undefined) => {
      if (!values && currentValue.length === 0) return;
      if (values && areArraysEqual(values, currentValue)) return;

      setCurrentValue(values ?? []);
      const formDataValues = values?.map(v => v?.value);
      onChangeRef.current(formDataValues?.[0]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChangeRef],
  );

  const [resolvedValues, setResolvedValues] = useState<
    TextFieldValue[] | undefined
  >(undefined);
  useEffect(() => {
    const resolveValues = async () => {
      if (defaultValue && !currentValue.length) {
        const resolved = await Promise.all(
          await loadInitialValue(defaultValue),
        );

        setResolvedValues(resolved);
      }
    };

    resolveValues();
  }, [defaultValue, currentValue, loadInitialValue]);

  return (
    <WbGeneralPurposeEntityPicker
      title={title}
      description={description}
      availableKinds={availableKinds}
      freeSolo={freeSolo}
      required={required}
      disabled={disabled}
      providerMapper={providerMapper}
      onSelect={handleSelect}
      value={currentValue ?? []}
      resolvedValues={resolvedValues}
    />
  );
};
